import { useCallback, useState } from 'react';
import { useTracking } from 'react-tracking';
import { useDispatch } from 'react-redux';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { Portal, usePreventWindowScroll } from '@simplywallst/ui-core';

import { useIsMobileScreen } from '@/hooks/useMedia';

import ActionSheet, { Background, Sheet } from '@/components/ActionSheet';
import {
  getTransactionFromFieldValues,
  useAddTransactionToPortfolio,
  useTransactionForm,
  TransactionForm,
} from '../../../../components/AddTransaction';
import { ROUTE_PORTFOLIO } from '@/constants/routes';
import { useCreateTransactionPortfolio } from '@/hooks/portfolio/useCreateTransactionPortfolio';
import { InfoBanner } from './InfoBanner';

const sheetWidth = 700;

type Props = {
  onClose: () => void;
};

export const AddTransactionsModal = ({ onClose }: Props) => {
  const [open, setOpen] = useState(true);
  const [portfolioId, setPortfolioId] = useState<string | null>(null);
  const [lastSavedTx, setLastSavedTx] = useState<ReturnType<
    typeof getTransactionFromFieldValues
  > | null>(null);

  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { trackEvent } = useTracking({
    subject: 'addTransactionModal',
    portfolioId,
  });

  const dispatch = useDispatch();

  usePreventWindowScroll();

  const createTxPortfolioMutationState = useCreateTransactionPortfolio();
  const addTxMutationState = useAddTransactionToPortfolio();
  const formState = useTransactionForm({
    initialValues: {
      type: 'BUY',
    },
  });

  const handleClose = useCallback(() => {
    trackEvent({ modifier: 'closeIcon', action: 'click' });
    if (portfolioId !== null) {
      dispatch({
        type: ROUTE_PORTFOLIO,
        payload: { portfolioId },
      });
      return;
    }
    onClose();
  }, [dispatch, onClose, portfolioId, trackEvent]);

  const createPortfolioAndAppendTx = useCallback(
    async (
      transaction: NonNullable<
        ReturnType<typeof getTransactionFromFieldValues>
      >,
      onSuccess: (portfolioId: string) => void
    ) => {
      try {
        const {
          createTransactionPortfolio: { id },
        } = await createTxPortfolioMutationState.mutateAsync({
          name: 'Transactions',
        });
        await addTxMutationState.mutateAsync({
          id,
          transaction,
          triggerRecalculation: true,
        });
        setPortfolioId(id);
        onSuccess(id);
      } catch (e) {
        console.error(e);
      }
    },
    [addTxMutationState, createTxPortfolioMutationState]
  );

  const handleSave = useCallback(async () => {
    trackEvent({ modifier: 'save', action: 'click' });
    const isValid = formState.validate();
    if (isValid === false) return;
    const transaction = getTransactionFromFieldValues(formState.values);
    if (transaction === null) return;

    // if portfolio is already created, just append the tx
    if (portfolioId) {
      addTxMutationState.mutate(
        {
          id: portfolioId,
          transaction,
          triggerRecalculation: true,
        },
        {
          onSuccess() {
            dispatch({
              type: ROUTE_PORTFOLIO,
              payload: { portfolioId },
            });
          },
        }
      );
      return;
    }

    // if not, first create the portfolio before appending txs
    void createPortfolioAndAppendTx(transaction, (newPortfolioId) => {
      dispatch({
        type: ROUTE_PORTFOLIO,
        payload: { portfolioId: newPortfolioId },
      });
    });
  }, [
    trackEvent,
    formState,
    portfolioId,
    createPortfolioAndAppendTx,
    addTxMutationState,
    dispatch,
  ]);

  const handleSaveAndAddMore = useCallback(async () => {
    trackEvent({ modifier: 'saveAndAddMore', action: 'click' });
    const isValid = formState.validate();
    if (isValid === false) return;
    const transaction = getTransactionFromFieldValues(formState.values);
    if (transaction === null) return;

    // if portfolio is already created, just append the tx
    if (portfolioId) {
      addTxMutationState.mutate(
        {
          id: portfolioId,
          transaction,
          triggerRecalculation: true,
        },
        {
          onSuccess() {
            setLastSavedTx(transaction);
            formState.reset();
          },
        }
      );
      return;
    }

    // if not, create the portfolio first then append tx
    void createPortfolioAndAppendTx(transaction, () => {
      setLastSavedTx(transaction);
      formState.reset();
    });
  }, [
    trackEvent,
    formState,
    portfolioId,
    createPortfolioAndAppendTx,
    addTxMutationState,
  ]);

  const mutationState = {
    ...addTxMutationState,
    isPending:
      addTxMutationState.isPending || createTxPortfolioMutationState.isPending,
  } as ReturnType<typeof useAddTransactionToPortfolio>;

  return (
    <>
      {isMobileScreen ? (
        <Portal>
          <div
            className="fixed left-0 top-0 z-[9999] size-full bg-legacy-noir"
            data-testid="add-transaction-modal"
          >
            <div className="pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]">
              <div className="grid h-[calc(100svh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] grid-rows-[1fr]">
                <TransactionForm
                  formState={formState}
                  mutationState={mutationState}
                  savedTicker={`${lastSavedTx?.exchangeCode}:${lastSavedTx?.symbol}`}
                  onClose={handleClose}
                  onSave={handleSave}
                  onSaveAndAddMore={handleSaveAndAddMore}
                  infoBanner={<InfoBanner />}
                />
              </div>
            </div>
          </div>
        </Portal>
      ) : (
        <ActionSheet
          open={open}
          onClose={() => setOpen(false)}
          onClosed={onClose}
        >
          <Background />
          <Sheet
            sheetLayout={{
              'max-width': `${sheetWidth}px`,
              left: `max(0px, calc(50vw - ${sheetWidth / 2}px))`,
            }}
          >
            <div
              className="grid grid-rows-[calc(50px+env(safe-area-inset-top))]"
              data-testid="add-transaction-action-sheet"
            >
              <div className="row-start-2 grid rounded-t-md bg-legacy-noir">
                <TransactionForm
                  formState={formState}
                  mutationState={mutationState}
                  savedTicker={`${lastSavedTx?.exchangeCode}:${lastSavedTx?.symbol}`}
                  onClose={handleClose}
                  onSave={handleSave}
                  onSaveAndAddMore={handleSaveAndAddMore}
                  infoBanner={<InfoBanner />}
                />
              </div>
            </div>
          </Sheet>
        </ActionSheet>
      )}
    </>
  );
};
